import { type BoxProps } from '@chakra-ui/react-v2';
import { Formik, Form, Field, type FormikState } from 'formik';
import {
  cloneElement,
  type DetailedReactHTMLElement,
  forwardRef,
  type ReactNode,
} from 'react';

import {
  Text,
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverFooter,
  PopoverTrigger,
  Button,
  PopoverButtonGroup,
  Divider,
  FormControl,
  FormLabel,
  Portal,
  useDisclosure,
  type SortDirection,
} from '@frontend/legacy-components';
import { FilterIcon } from '@frontend/legacy-components/src/components/mediaAndIcons/icons/iconSet';
import { noop } from '@frontend/utils';

import {
  TableFilterPopoverBody,
  TableFilterPopoverSortButtons,
} from './FilterPopoverContents';
import {
  TableFilterHeaderCellButton,
  type TableFilterHeaderCellButtonProps,
  TableFilterHeaderCellSortDisplay,
  TableFilterHeaderCellContent,
} from './HeaderCellContents';

export type TableHeaderCellContentPrefabProps = BoxProps & {
  columnDataIndex: string;
  columnTitle: ReactNode;
  sortPossibleDirections: SortDirection[];
  filterInput?: DetailedReactHTMLElement<any, HTMLElement> | null;
  filterEmptyValue?: unknown;
  filterFormatter?: (output: unknown) => unknown;
  onApplySortAndFilter?: (
    columnDataIndex: string,
    sort?: SortDirection | null,
    filter?: unknown,
  ) => void;
  onIncrementSort?: (
    columnDataIndex: string,
    sortPossibleDirections: SortDirection[],
  ) => void;
  sortPriority?: number;
  sortDirection?: SortDirection | null;
  filterValue?: unknown;
  headerButtonProps?: TableFilterHeaderCellButtonProps;
};

/**
 * A prefab for delivering standardized header cell sort/filter logic.
 * Should this not work for your purpose, you can clone its logic locally since all the subcomponents and hook are exported from the library.
 * @param columnDataIndex The dataIndex to use within useTableSortingV1's sortStore.
 * @param columnTitle The title of the column to use in this header cell.
 * @param sortPossibleDirections the array of possible sort directions.
 * @param filterInput A react component to serve as the filter inside the filter popover.  Leave empty to disable filtering.
 * @param filterEmptyValue Value to be passed to the `value` prop of the `filterInput` when it is considered empty
 * @param filterFormatter A function that translates the output of the `filterInput` `onChange` into a value safe for its `value` prop.
 * @param onApplySortAndFilter Callback fired when the popover apply and clear buttons are clicked (dataIndex, sortDirection, filterValue) => void.
 * @param onIncrementSort Callback fired when header cell sort button is clicked (dataIndex, ('ascending' | "descending")[]) => void.
 * @param sortPriority Current sort priority.
 * @param sortDirection Current sort direction.
 * @param filterValue Current filter value.
 * @param headerButtonProps Props given to the header cell button.
 * @returns a component containing sorting and filter buttons for a Table Cell header.
 */
export const TableHeaderCellContentPrefab = forwardRef<
  HTMLDivElement,
  TableHeaderCellContentPrefabProps
>(
  (
    {
      columnDataIndex,
      columnTitle,
      sortPossibleDirections = [],
      filterInput,
      filterEmptyValue,
      filterFormatter = (output) => output,
      onApplySortAndFilter = noop,
      onIncrementSort = noop,
      sortPriority,
      sortDirection,
      filterValue,
      headerButtonProps = {},
      ...restProps
    },
    ref,
  ) => {
    const { isOpen, onClose, onToggle } = useDisclosure();
    return (
      <TableFilterHeaderCellContent ref={ref} {...restProps}>
        {/* Sort Button */}
        <TableFilterHeaderCellButton
          isDisabled={sortPossibleDirections?.length === 0 || false}
          px="0.75rem"
          flexGrow={1}
          onClick={() =>
            onIncrementSort(columnDataIndex, sortPossibleDirections)
          }
          {...headerButtonProps}
        >
          <Text fontWeight="inherit" noOfLines={1} m={0}>
            {columnTitle}
          </Text>
          <TableFilterHeaderCellSortDisplay
            priority={sortPriority}
            sortDirection={sortDirection}
            sortPossibleDirections={sortPossibleDirections}
          />
        </TableFilterHeaderCellButton>
        {/* Popover local component */}

        {filterInput != null && (
          <Formik
            enableReinitialize
            initialValues={{
              sort: sortDirection,
              filter: filterValue != null ? filterValue : filterEmptyValue,
            }}
            onSubmit={(values, actions) => {
              onApplySortAndFilter(columnDataIndex, values.sort, values.filter);
              const formikValues = values as Partial<
                FormikState<{
                  sort: SortDirection | null | undefined;
                  filter: unknown;
                }>
              >;
              actions.resetForm(formikValues);
              onClose();
            }}
          >
            {({ dirty, values, setFieldValue, resetForm }) => (
              <Popover
                size="sm"
                isOpen={isOpen}
                onClose={() => {
                  resetForm();
                  onClose();
                }}
              >
                <PopoverTrigger>
                  {/* Filter button */}
                  <TableFilterHeaderCellButton
                    onClick={onToggle}
                    justifyContent="center"
                    width="2rem"
                  >
                    <FilterIcon
                      boxSize="1.125rem"
                      color={
                        filterValue != null && filterValue != filterEmptyValue
                          ? 'interaction.main'
                          : 'feature.placeholder'
                      }
                    />
                  </TableFilterHeaderCellButton>
                </PopoverTrigger>

                <Portal>
                  <PopoverContent>
                    <PopoverHeader>Filter: {columnTitle}</PopoverHeader>
                    {/* Sort & filter form */}
                    <Form>
                      <TableFilterPopoverBody>
                        {/* SORT FIELD ----------------------------------------------------- */}
                        {sortPossibleDirections.length > 0 && (
                          <>
                            <Field name="sort" id={`${columnDataIndex}_sort`}>
                              {({
                                field: { value },
                                form: { setFieldValue },
                              }: {
                                field: { value: SortDirection };
                                form: {
                                  setFieldValue: (
                                    field: string,
                                    value: SortDirection | null,
                                  ) => void;
                                };
                              }) => (
                                <FormControl>
                                  <FormLabel htmlFor="sort">Sort</FormLabel>
                                  <TableFilterPopoverSortButtons
                                    sortPossibleDirections={
                                      sortPossibleDirections
                                    }
                                    value={value}
                                    onChange={(output: SortDirection | null) =>
                                      setFieldValue('sort', output)
                                    }
                                  />
                                </FormControl>
                              )}
                            </Field>
                            <Divider />
                          </>
                        )}
                        {/* ---------------------------------------------------------------- */}

                        {/* FILTER FIELD --------------------------------------------------- */}
                        <Field name="filter" id={`${columnDataIndex}_filter`}>
                          {({
                            field: { value },
                            form: { setFieldValue },
                          }: {
                            field: { value: unknown };
                            form: {
                              setFieldValue: (
                                field: string,
                                value: unknown,
                              ) => void;
                            };
                          }) => (
                            <FormControl>
                              <FormLabel htmlFor="filter">Filter</FormLabel>
                              {cloneElement(filterInput, {
                                size: 'sm',
                                value,
                                onChange: (output: unknown) => {
                                  setFieldValue(
                                    'filter',
                                    filterFormatter(output),
                                  );
                                },
                                ...filterInput.props,
                              })}
                            </FormControl>
                          )}
                        </Field>
                        {/* ---------------------------------------------------------------- */}
                      </TableFilterPopoverBody>
                      <PopoverFooter>
                        {/* Cancel & submit form buttons */}
                        <PopoverButtonGroup>
                          <Button
                            variant="secondary"
                            type="reset"
                            onClick={() => {
                              // Manual reset procedure.
                              setFieldValue('sort', null);
                              setFieldValue('filter', filterEmptyValue);
                              onApplySortAndFilter(columnDataIndex, null, null);
                              resetForm({
                                values: {
                                  sort: null,
                                  filter: filterEmptyValue,
                                },
                              });
                              onClose();
                            }}
                            isDisabled={
                              values.sort === null &&
                              values.filter === filterEmptyValue
                            }
                          >
                            clear
                          </Button>
                          <Button
                            variant="primary"
                            isDisabled={!dirty}
                            type="submit"
                          >
                            apply
                          </Button>
                        </PopoverButtonGroup>
                      </PopoverFooter>
                    </Form>
                  </PopoverContent>
                </Portal>
              </Popover>
            )}
          </Formik>
        )}
      </TableFilterHeaderCellContent>
    );
  },
);
TableHeaderCellContentPrefab.displayName = 'TableHeaderCellContentPrefab';
